import { useCallback, useContext } from 'react';
import { DropdownItem, FormGroup, Spinner } from 'reactstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { Modal } from '../../../../common/Modal';
import ValidatedSelect from '../../../../common/forms/ValidatedSelect';
import { ApiRoutes } from '../../../../Common';
import { DispatchContext } from '../../DispatchContext';
import { toasty } from '../../../../common/forms/FormElements';
import { useJobQueryKey } from '../../common/useJobQueryKey.hook';

const TransferJobModalBody = ({ job, dispatchLocations }) => {
    const { data, isLoading } = useQuery({
        queryKey: ['contractDispatchLocations', job.contractId],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.contractDispatchLocations(job.contractId)),
    });

    if (isLoading) {
        return (
            <div className="text-center p-3">
                <Spinner color="primary" />
            </div>
        );
    }

    const contractLocationIds = data.map((cl) => cl.value);
    const targetOptions = (dispatchLocations ?? []).filter(
        (loc) => loc.value !== job.dispatchLocationId
            && contractLocationIds.includes(loc.value),
    );

    return (
        <>
            <FormGroup>
                <label>{`Job: ${job.name} - ${job.number}`}</label>
            </FormGroup>

            <ValidatedSelect
                placeholder="Select Target Office"
                label="Transfer to Office"
                id="targetOfficeId"
                name="targetOfficeId"
                hideClear
                required
                options={targetOptions}
                error="A target office is required."
                TipComponentProps={{
                    label: 'A job can only be transferred to another dispatch office on the same contract. If the below list is empty then that means there are no other dispatch offices on the contract.',
                }}
            />
        </>
    );
};

const defaultOnSuccess = () => { };

export const useHandleTransferJob = ({
    job, day, dispatchLocations, onSuccess = defaultOnSuccess,
}) => {
    const handleTransferJobSubmit = useMutation({
        mutationFn: (values) => {
            const assignment = job?.assignments[day] ?? {};
            const model = {
                jobId: assignment?.jobId ?? job.id,
                day,
                targetDispatchOfficeLocationId: values.targetOfficeId,
                sendNotifications: true,
            };

            return httpClient.post(
                ApiRoutes.dispatch.transferJobDay(),
                model,
            );
        },
        onSuccess: () => {
            toasty.success('Job day transferred successfully.');
            onSuccess();
        },
        onError: () => {
            toasty.error('Something went wrong.', 'Please try again.');
        },
    });

    const handleTransferJob = useCallback(() => {
        Modal.show({
            title: 'Transfer Job Day',
            SaveButtonProps: {
                label: 'Transfer',
                savingLabel: 'Please Wait...',
                color: 'primary',
            },
            onSubmit: handleTransferJobSubmit.mutateAsync,
            body: () => (
                <TransferJobModalBody
                    job={job}
                    dispatchLocations={dispatchLocations}
                />
            ),
        });
    }, [job, dispatchLocations, handleTransferJobSubmit.mutateAsync]);

    return handleTransferJob;
};

export const TransferJob = ({ day, ...job }) => {
    const { dispatchLocations } = useContext(DispatchContext);
    const { queryKey } = useJobQueryKey();

    const queryClient = useQueryClient();

    const handleSuccess = useCallback(() => {
        queryClient.invalidateQueries({ queryKey });
    }, [queryClient, queryKey]);

    const handleTransferJob = useHandleTransferJob({
        job, day, dispatchLocations, onSuccess: handleSuccess,
    });

    return (
        <DropdownItem
            className="text-warning"
            onClick={handleTransferJob}
        >
            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
            <span>Transfer Job</span>
        </DropdownItem>
    );
};

